import styled from '@emotion/styled';
import axios from 'axios';

import type { Organization } from '@hotjar/organization';
import { tokens, Button, Text, useNotify } from '@hotjar/hotsauce';
import { trackClick, utils } from '@hotjar/tracking';
import { useCurrentSite } from '@hotjar/site';
import { useTranslation } from '@hotjar/translations';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
`;

interface LogRequestSectionProps {
  organization: Organization;
}

export const LogRequestSection = ({ organization }: LogRequestSectionProps): JSX.Element => {
  const { t } = useTranslation('common');
  const { showToast } = useNotify();
  const { data: currentSite } = useCurrentSite();

  const downloadLogAsCsv = async () => {
    if (currentSite) {
      trackClick({
        name: 'clicked_download_log_user_lookup_requests',
        uiPosition: 'button_download.log.section',
        groupData: utils.extractSiteGroupingProps(currentSite),
        data: {
          permission_level: utils.extractPermissionLevel(currentSite),
        },
      });
    }
    try {
      const path = `api/v1/accounts/${organization.accountId}/organizations/${organization.id}/visitor-lookup/logs?format=csv`;
      const response = await axios({
        url: path,
        method: 'GET',
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_lookup_requests_log-${organization.id}.csv`);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      showToast({ text: 'Something went wrong', kind: 'danger' });
    }
  };

  return (
    <ContentWrapper>
      <Text as="h2" textType="header5">
        {t('visitor-lookup.request-log-section.title')}
      </Text>
      <Text as="p" textType="body2">
        {t('visitor-lookup.request-log-section.description', {
          organizationName: organization.name,
        })}
      </Text>

      <div>
        <Button
          id="download_log_btn"
          type="button"
          kind="ghost"
          icon="download"
          onClick={downloadLogAsCsv}
        >
          {t('visitor-lookup.request-log-section.download-button')}
        </Button>
      </div>
    </ContentWrapper>
  );
};
